// core
import Moment from 'moment';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import _ from 'lodash';

// other components
import { Box, Card, CardContent } from '@mui/material';
import { makeStyles } from '@mui/styles';

import JsonViewer from '../../common/JsonViewer';
import { ExpandableRow } from '../../components/tables/dev-express/table-components/rowComponents';
import { ActivityBarChart } from './ActivityBarChartV2';
import { ColumnChooser } from '../../components/tables/dev-express/table-components/toolbar/ColumnChooser';
import { RefreshButton } from '../../components/tables/dev-express/table-components/toolbar/RefreshButton';
import { PrimaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/PrimaryToolbar';
import { SecondaryToolbar } from '../../components/tables/dev-express/table-components/toolbar/SecondaryToolbar';
import { RowDetailWrapper } from '../../components/tables/dev-express/table-components/RowDetailWrapper';
import { TableFilterChipsV2 } from '../../components/tables/tableFilters/v2/TableFilterChipsV2';
import { TableStateProvider } from '../../components/tables/dev-express/TableStateContext';
import { CsvDownloadButton } from '../../components/tables/dev-express/table-components/toolbar/CsvDownloadButton';
import { BaseTable } from '../../components/tables/dev-express/BaseTable';
import { RemotePagingPlugin } from '../../components/tables/dev-express/plugins/RemotePagingPlugin';
import { RowDetailPlugin } from '../../components/tables/dev-express/plugins/RowDetailPlugin';

// hooks, services
import { fetchActivityDetails } from '../../api/activity.api';
import { ColumnResizingPlugin } from '../../components/tables/dev-express/plugins/ColumnResizingPlugin';
import { useQueryHandlers } from '../../components/tables/dev-express/hooks/useQueryHandlers';
import { useBackfill } from '../../components/tables/dev-express/hooks/useBackfill';
import { useTableState } from '../../components/tables/dev-express/hooks/useTableState';
import { useSavedSettings } from '../../components/tables/dev-express/hooks/useSavedSettings';
import { useUiSettingsAutosave } from '../../components/tables/dev-express/hooks/useUiSettingsAutosave';
import { useQueryParams } from '../../components/providers/QueryParamsProvider';
import { useAPIErrorHandler } from '../../components/hooks/useAPIErrorHandler';
import { FORMAT_DATE_AND_TIME, toUniversalDate } from '../../utils/date.utils';

import NewThemeProvider from '../../providers/NewThemeProvider';
import TableThemeProvider from '../../providers/TableThemeProvider';
import { URLDateRangePicker } from '../../components/lib/DateRangePicker/withQueryParamController';
import { SearchFieldWithQueryParamController } from '../../components/lib/SearchField/withQueryParamSubscriber';
import { ACTIVITY } from '../../components/tables/table_constants';
import { useGetBackfill } from '../../utils/backfill.utills';
import { REDACTED_FIELD } from '../../config/logrocket';

const tableId = ACTIVITY;

export const ActivityListing = () => {
  const { query } = useQueryParams();
  const queryClient = useQueryClient();

  const { uiSettings } = useSavedSettings(tableId, columnConfig);
  const backfill = useGetBackfill(tableId);
  useBackfill(backfill, uiSettings);
  const { queryHandlers } = useQueryHandlers(query);
  const { state, columns, handlers } = useTableState(columnConfig, {
    uiSettings,
    tableId,
  });

  useUiSettingsAutosave(
    tableId,
    {
      columns,
      columnWidths: state.columnWidths,
      columnOrder: state.columnOrder,
      ...query,
    },
    ['before', 'after']
  );

  const handleAPIError = useAPIErrorHandler();
  const { data = {}, isFetching } = useQuery({
    queryKey: [tableId, query],
    queryFn: () =>
      fetchActivityDetails(query).then((resp: any) => {
        return {
          totalCount: resp.data.totalElements,
          rows: resp.data.content,
        };
      }),
    keepPreviousData: true,
    placeholderData: undefined,
    onError: handleAPIError(
      'There was a problem loading the page, please try again in a few minutes'
    ),
  });

  const onRefetch = () => {
    queryClient.invalidateQueries([tableId]);
  };

  return (
    <NewThemeProvider>
      <TableThemeProvider>
        <PrimaryToolbar sticky={false} justifyContent="flex-end">
          <Box display="flex" width="60%" alignItems="center" gap="8px">
            <SearchFieldWithQueryParamController />

            <URLDateRangePicker />
          </Box>
        </PrimaryToolbar>
        <ActivityBarChart q={query} />

        <TableStateProvider
          state={{ ...state, ...query }}
          handlers={{ ...handlers, ...queryHandlers }}
          columns={columns}
          rows={_.get(data, 'rows', [])}
          tableId={tableId}
          loading={isFetching}
        >
          <SecondaryToolbar>
            <TableFilterChipsV2 tableId={tableId} />
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <ColumnChooser />
              <CsvDownloadButton />
              <RefreshButton onClick={onRefetch} loading={isFetching} />
            </Box>
          </SecondaryToolbar>

          <BaseTable RowComponent={ExpandableRow}>
            <RemotePagingPlugin totalCount={_.get(data, 'totalCount', 0)} />
            <RowDetailPlugin rowDetailComponent={RowDetail} />
            <ColumnResizingPlugin />
          </BaseTable>
        </TableStateProvider>
      </TableThemeProvider>
    </NewThemeProvider>
  );
};

const columnConfig = {
  id: {
    name: 'id',
    title: 'ID',
    width: '20%',
  },
  eventType: {
    name: 'eventType',
    title: 'Event Type',
    width: '15%',
    multiline: false,
  },
  eventSummary: {
    name: 'eventSummary',
    title: 'Event Summary',
    className: `${REDACTED_FIELD}`,
    width: '40%',
  },
  modified: {
    name: 'modified',
    title: 'Date',
    width: '12%',
    getCellValue: (row: any) =>
      toUniversalDate(row.modified, {
        format: FORMAT_DATE_AND_TIME,
      }),
  },
  created: {
    name: 'created',
    title: 'Created',
    width: '12%',
    sort: false,
    getCellValue: (row: any) =>
      row.modified ? Moment(row.modified).fromNow() : '-',
  },
};

const RowDetail = ({ ...props }) => {
  const classes = useExpandedRowStyles();
  return (
    <RowDetailWrapper tableId={tableId}>
      <Card className={classes.rowDetailCard}>
        <CardContent>
          <JsonViewer jsonData={_.get(props.row, 'eventDetails', '')} />
        </CardContent>
      </Card>
    </RowDetailWrapper>
  );
};

const useExpandedRowStyles = makeStyles(() => ({
  rowDetailCard: {
    paddingBottom: 10,
    width: '100%',
  },
}));
